import type { Block, BlockBackground, FeatureGridItem } from "@/types";
import { TextField, TextAreaField, Button } from "@/components/ui";
import { parseEmbed } from "@/renderer/embed";
import { ImageUploader } from "./ImageUploader";
import { BackgroundControl } from "./BackgroundControl";

/**
 * Per-type property editor (§5). Renders only editing UI — never block render
 * logic (that lives in the shared renderer). The shared Background control
 * (§5A) is appended to every type.
 */
export function BlockEditor({ block, onChange }: { block: Block; onChange: (b: Block) => void }) {
  // Merge a props patch immutably (cast: block is a narrowed union member).
  const patch = (p: Record<string, unknown>) =>
    onChange({ ...block, props: { ...block.props, ...p } } as Block);
  const setBackground = (bg: BlockBackground | undefined) => onChange({ ...block, background: bg });

  return (
    <div className="block-editor">
      <div className="block-editor__fields">{renderFields(block, patch)}</div>
      <BackgroundControl background={block.background} onChange={setBackground} />
    </div>
  );
}

function MarkdownHint() {
  return <p className="block-editor__hint">Markdown supported.</p>;
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="block-editor__toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="block-editor__segmented">
      <span className="block-editor__seg-label">{label}</span>
      <div className="block-editor__seg-options">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`block-editor__seg${value === o.value ? " is-active" : ""}`}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function renderFields(block: Block, patch: (p: Record<string, unknown>) => void) {
  switch (block.type) {
    case "cover":
      return (
        <>
          <TextField label="Title" value={block.props.title} onChange={(e) => patch({ title: e.target.value })} />
          <TextField
            label="Subtitle"
            value={block.props.subtitle ?? ""}
            onChange={(e) => patch({ subtitle: e.target.value })}
          />
        </>
      );

    case "rich_text":
      return (
        <>
          <TextField
            label="Heading"
            value={block.props.heading ?? ""}
            onChange={(e) => patch({ heading: e.target.value })}
          />
          <TextAreaField
            label="Body"
            value={block.props.body_md}
            onChange={(e) => patch({ body_md: e.target.value })}
          />
          <MarkdownHint />
        </>
      );

    case "image":
      return (
        <>
          <ImageUploader value={block.props.asset_url} onChange={(url) => patch({ asset_url: url })} />
          <TextField label="Alt text" value={block.props.alt ?? ""} onChange={(e) => patch({ alt: e.target.value })} />
          <TextField
            label="Caption"
            value={block.props.caption ?? ""}
            onChange={(e) => patch({ caption: e.target.value })}
          />
          <Segmented
            label="Layout"
            value={block.props.layout ?? "full"}
            options={[
              { value: "full", label: "Full width" },
              { value: "inset", label: "Inset" },
            ]}
            onChange={(v) => patch({ layout: v })}
          />
        </>
      );

    case "pricing":
      return (
        <>
          <TextField
            label="Heading"
            value={block.props.heading ?? ""}
            onChange={(e) => patch({ heading: e.target.value })}
          />
          <p className="block-editor__hint">
            Line items are managed in the quote’s pricing section; this block renders them.
          </p>
        </>
      );

    case "terms":
      return (
        <>
          <TextField
            label="Heading"
            value={block.props.heading ?? ""}
            onChange={(e) => patch({ heading: e.target.value })}
          />
          <TextAreaField
            label="Body"
            value={block.props.body_md}
            onChange={(e) => patch({ body_md: e.target.value })}
          />
          <MarkdownHint />
        </>
      );

    case "accept":
      return (
        <>
          <Toggle
            label="Require the client's name to accept"
            checked={block.props.require_name}
            onChange={(v) => patch({ require_name: v })}
          />
          <Toggle
            label="Ask for a reason when declining"
            checked={block.props.ask_decline_reason ?? true}
            onChange={(v) => patch({ ask_decline_reason: v })}
          />
        </>
      );

    case "author":
      return (
        <>
          <TextField label="Name" value={block.props.name} onChange={(e) => patch({ name: e.target.value })} />
          <TextField label="Role" value={block.props.role ?? ""} onChange={(e) => patch({ role: e.target.value })} />
          <TextAreaField
            label="Note"
            value={block.props.note ?? ""}
            onChange={(e) => patch({ note: e.target.value })}
          />
          <ImageUploader label="Photo" value={block.props.photo_url} onChange={(url) => patch({ photo_url: url })} />
        </>
      );

    case "quote":
      return (
        <>
          <TextAreaField
            label="Testimonial"
            value={block.props.text}
            onChange={(e) => patch({ text: e.target.value })}
          />
          <TextField
            label="Attribution"
            value={block.props.attribution ?? ""}
            onChange={(e) => patch({ attribution: e.target.value })}
          />
        </>
      );

    case "split":
      return (
        <>
          <TextField
            label="Heading"
            value={block.props.heading ?? ""}
            onChange={(e) => patch({ heading: e.target.value })}
          />
          <TextAreaField
            label="Body"
            value={block.props.body_md}
            onChange={(e) => patch({ body_md: e.target.value })}
          />
          <MarkdownHint />
          <ImageUploader value={block.props.asset_url} onChange={(url) => patch({ asset_url: url })} />
          <TextField label="Alt text" value={block.props.alt ?? ""} onChange={(e) => patch({ alt: e.target.value })} />
          <Segmented
            label="Image side"
            value={block.props.image_side ?? "left"}
            options={[
              { value: "left", label: "Left" },
              { value: "right", label: "Right" },
            ]}
            onChange={(v) => patch({ image_side: v })}
          />
        </>
      );

    case "feature_grid":
      return <FeatureGridFields items={block.props.items} heading={block.props.heading} patch={patch} />;

    case "embed":
      return <EmbedFields url={block.props.url} caption={block.props.caption} patch={patch} />;

    default:
      return null;
  }
}

function FeatureGridFields({
  heading,
  items,
  patch,
}: {
  heading?: string;
  items: FeatureGridItem[];
  patch: (p: Record<string, unknown>) => void;
}) {
  const list = items ?? [];
  const setItem = (i: number, next: Partial<FeatureGridItem>) =>
    patch({ items: list.map((it, idx) => (idx === i ? { ...it, ...next } : it)) });
  const addItem = () => patch({ items: [...list, { title: "", body: "" }] });
  const removeItem = (i: number) => patch({ items: list.filter((_, idx) => idx !== i) });

  return (
    <>
      <TextField label="Heading" value={heading ?? ""} onChange={(e) => patch({ heading: e.target.value })} />
      <div className="block-editor__items">
        {list.map((item, i) => (
          <div className="block-editor__item" key={i}>
            <div className="block-editor__item-head">
              <span className="block-editor__item-n">Item {i + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => removeItem(i)}>Remove</Button>
            </div>
            <TextField label="Title" value={item.title} onChange={(e) => setItem(i, { title: e.target.value })} />
            <TextField label="Body" value={item.body ?? ""} onChange={(e) => setItem(i, { body: e.target.value })} />
          </div>
        ))}
      </div>
      <Button variant="secondary" size="sm" onClick={addItem}>Add item</Button>
    </>
  );
}

function EmbedFields({
  url,
  caption,
  patch,
}: {
  url: string;
  caption?: string;
  patch: (p: Record<string, unknown>) => void;
}) {
  const parsed = parseEmbed(url);
  const invalid = url.trim() !== "" && !parsed;
  return (
    <>
      <TextField
        label="Video URL (YouTube or Vimeo)"
        value={url}
        onChange={(e) => patch({ url: e.target.value })}
        error={invalid ? "Unsupported video link — use a YouTube or Vimeo URL." : undefined}
        hint={parsed ? `Recognised: ${parsed.provider}` : undefined}
        placeholder="https://vimeo.com/…"
      />
      <TextField
        label="Caption"
        value={caption ?? ""}
        onChange={(e) => patch({ caption: e.target.value })}
      />
    </>
  );
}
